import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { sendFile } from '../lib/upload';
import { fileSize } from '../lib/format';
import { useDocumentTitle, useLoad, useProjectEvents } from '../lib/hooks';
import type { Comment, Item, Person, Project, ReviewStatus, Viewport } from '../lib/types';
import { useAuth } from '../state/auth';
import { AppHeader } from '../components/AppShell';
import { Icon, type IconName } from '../components/Icon';
import { Dialog, ErrorState, Field, Menu, PageLoading } from '../components/ui';
import { useToast } from '../components/toast';
import { CommentSidebar, type CommentFilter } from '../review/CommentSidebar';
import { ImageCanvas } from '../review/ImageCanvas';
import { VideoCanvas } from '../review/VideoCanvas';
import { WebsiteCanvas } from '../review/WebsiteCanvas';
import { useComments, type NewComment } from '../review/useComments';

const TYPE_ICON: Record<Item['type'], IconName> = { website: 'globe', image: 'image', video: 'video' };
const VIEWPORTS: { value: Viewport; icon: IconName; label: string }[] = [
  { value: 'desktop', icon: 'monitor', label: 'Desktop, 1440 wide' },
  { value: 'tablet', icon: 'tablet', label: 'Tablet, 768 wide' },
  { value: 'mobile', icon: 'smartphone', label: 'Mobile, 390 wide' },
];

function NewVersionDialog({ item, onClose, onDone }: { item: Item; onClose: () => void; onDone: (item: Item) => void }) {
  const { meta } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      const result = await sendFile<{ item: Item }>({
        projectId: item.projectId,
        purpose: 'version',
        itemId: item.id,
        file,
        note,
        chunkBytes: meta?.uploadChunkBytes ?? 32 * 1024 * 1024,
        onProgress: setProgress,
      });
      onDone(result.item);
    } catch (err) {
      setError(errorMessage(err));
      setProgress(null);
    }
  };

  return (
    <Dialog
      title="Upload a new version"
      description="Earlier versions and their revisions stay available. Approval resets for the new file."
      onClose={onClose}
      dismissible={progress === null}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={progress !== null}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={!file || progress !== null}>
            {progress !== null && <span className="spinner" />}
            Upload version {(item.versions?.[0]?.versionNo ?? 1) + 1}
          </button>
        </>
      }
    >
      <Field label={item.type === 'video' ? 'Video file' : 'Image file'} hint={file ? `${file.name} · ${fileSize(file.size)}` : undefined}>
        {(id) => <input id={id} className="input" type="file" accept={item.type === 'video' ? 'video/*' : 'image/*'} onChange={(event) => setFile(event.target.files?.[0] ?? null)} />}
      </Field>
      <Field label="What changed" hint="Optional. Shown next to the version.">
        {(id) => <input id={id} className="input" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Tightened the intro, new end card" />}
      </Field>
      {progress !== null && (
        <div className="progress" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${progress * 100}%` }} />
        </div>
      )}
      {error && (
        <div className="notice notice-error" role="alert">
          {error}
        </div>
      )}
    </Dialog>
  );
}

export function ReviewPage() {
  const { projectId = '', itemId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();
  const embed = params.get('embed') === '1';

  const loaded = useLoad(() => api.get<{ item: Item; project: Project }>(`/api/items/${itemId}`), [itemId]);
  const peopleLoad = useLoad(() => api.get<{ people: Person[] }>(`/api/projects/${projectId}/people`), [projectId]);
  const { comments, loading: commentsLoading, error: commentsError, reload: reloadComments, actions } = useComments(projectId, { itemId });

  const item = loaded.data?.item ?? null;
  const project = loaded.data?.project ?? null;
  const people = peopleLoad.data?.people ?? [];
  const canComment = !!project?.permissions?.canComment;
  const canManage = !!project?.permissions?.canManage;
  const canVersion = !!project?.permissions?.canVersion;
  useDocumentTitle(item?.title);

  const [mode, setMode] = useState<'browse' | 'comment'>('browse');
  const [filter, setFilter] = useState<CommentFilter>('open');
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [versionId, setVersionId] = useState<string | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const selectedId = params.get('comment');

  const select = useCallback(
    (id: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (id) next.set('comment', id);
          else next.delete('comment');
          return next;
        },
        { replace: true },
      );
      if (id) {
        actions.markRead(id);
        setSideOpen(true); // on a narrow screen the thread lives in the slide-over
      }
    },
    [setParams, actions],
  );

  useProjectEvents(projectId, (event) => {
    const resync = event.type === 'resync';
    if (resync || (event.type.startsWith('comment.') && (!event.itemId || event.itemId === itemId))) void reloadComments(true);
    // A new comment on an approved item puts it back in review, so the item is reloaded too.
    if (resync || ((event.type.startsWith('item.') || event.type === 'comment.created') && event.itemId === itemId)) void loaded.reload(true);
  });

  // A deep link to a resolved comment, or to one on an older version, should still show it.
  const selected = comments.find((c) => c.id === selectedId) ?? null;
  const versions = item?.versions ?? [];
  const version = versions.find((v) => v.id === versionId) ?? item?.currentVersion ?? null;
  const versionShownFor = useRef<string | null>(null);
  useEffect(() => {
    if (!selected) {
      versionShownFor.current = null;
      return;
    }
    actions.markRead(selected.id);
    // Once per selection, and only when the item (with its versions) has arrived too.
    if (versionShownFor.current === selected.id || !item) return;
    versionShownFor.current = selected.id;
    if (selected.versionId && versions.some((v) => v.id === selected.versionId) && selected.versionId !== version?.id) setVersionId(selected.versionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per selection, when its inputs first exist
  }, [selected?.id, item?.id]);

  // Stay on the version that was open. A teammate uploading a new one must not swap the
  // file under a half-written comment; the version menu shows that a newer one exists.
  const latestVersionId = item?.currentVersion?.id ?? null;
  const knownLatest = useRef<string | null>(null);
  useEffect(() => {
    if (!latestVersionId) return;
    if (knownLatest.current && knownLatest.current !== latestVersionId && versionId === knownLatest.current && !uploading) {
      toast.show('A new version was uploaded. Switch to it from the version menu.');
    }
    setVersionId((current) => current ?? latestVersionId);
    knownLatest.current = latestVersionId;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to a new latest version only
  }, [latestVersionId]);

  // Keyboard: R toggles revision mode, Escape leaves it.
  const modeRef = useRef(mode);
  modeRef.current = mode;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]') || document.querySelector('.dialog-scrim')) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if ((event.key === 'r' || event.key === 'R') && canComment) {
        setMode((current) => (current === 'comment' ? 'browse' : 'comment'));
      } else if (event.key === 'Escape') {
        if (modeRef.current === 'comment') setMode('browse');
        else select(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canComment, select]);

  const create = useCallback(
    async (input: NewComment): Promise<Comment> => {
      const comment = await actions.create(input);
      void loaded.reload(true);
      return comment;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload identity is stable per item
    [actions],
  );

  const setReview = async (status: ReviewStatus) => {
    if (!item || reviewBusy) return;
    setReviewBusy(true);
    try {
      const result = await api.post<{ item: Item }>(`/api/items/${item.id}/review`, { status });
      loaded.setData((current) => current && { ...current, item: result.item });
      toast.show(status === 'approved' ? 'Marked as approved.' : status === 'changes_requested' ? 'Marked as changes requested.' : 'Back in review.');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setReviewBusy(false);
    }
  };

  if (loaded.loading && !loaded.data) return <PageLoading label="Opening review" />;
  if (loaded.error || !item || !project) {
    return (
      <div className="app">
        {!embed && <AppHeader />}
        <ErrorState title="This review item is not available" message={loaded.error || 'It may have been removed, or you may not have access.'} onRetry={() => loaded.reload()} />
      </div>
    );
  }

  const linkFor = (comment: Comment) => `${location.origin}/projects/${project.id}/items/${item.id}?comment=${comment.id}`;
  const showResolved = filter !== 'open';
  const shared = {
    item,
    projectId: project.id,
    people,
    comments,
    selectedId,
    highlightId,
    mode,
    showResolved,
    canComment,
    onSelect: select,
    onHover: setHighlightId,
    onCreate: create,
  };

  return (
    <div className="app">
      {!embed && <AppHeader crumbs={[{ label: 'Projects', to: '/' }, { label: project.name, to: `/projects/${project.id}` }, { label: item.title }]} />}
      <div className="review">
        <div className="review-bar">
          <div className="review-title">
            {!embed && (
              <Link to={`/projects/${project.id}`} className="icon-btn" aria-label={`Back to ${project.name}`}>
                <Icon name="arrow-left" />
              </Link>
            )}
            <Icon name={TYPE_ICON[item.type]} className="muted" />
            <h1 className="truncate">{item.title}</h1>
          </div>

          {canComment && (
            <div className="segmented" role="group" aria-label="Mode">
              <button type="button" aria-pressed={mode === 'browse'} onClick={() => setMode('browse')} title="Use the page normally">
                <Icon name="cursor" /> Browse
              </button>
              <button type="button" className="is-accent" aria-pressed={mode === 'comment'} onClick={() => setMode('comment')} title="Click anywhere to add a revision (R)">
                <Icon name="pin" /> Add revision
              </button>
            </div>
          )}

          <div className="row" style={{ gap: 8, flex: '1 1 160px', justifyContent: 'flex-end' }}>
            {item.type === 'website' && (
              <div className="segmented" role="group" aria-label="Screen size">
                {VIEWPORTS.map((option) => (
                  <button key={option.value} type="button" aria-pressed={viewport === option.value} aria-label={option.label} title={option.label} onClick={() => setViewport(option.value)}>
                    <Icon name={option.icon} />
                  </button>
                ))}
              </div>
            )}

            {item.type !== 'website' && versions.length > 0 && (
              <div className="row" style={{ gap: 4 }}>
                <Icon name="layers" className="muted" />
                <select className="select select-inline" aria-label="Version" value={version?.id ?? ''} onChange={(event) => setVersionId(event.target.value)} style={{ border: '1px solid var(--border-medium)' }}>
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNo}
                      {v.id === item.currentVersion?.id ? ' (latest)' : ''}
                    </option>
                  ))}
                </select>
                {canVersion && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUploading(true)}>
                    <Icon name="upload" /> New version
                  </button>
                )}
              </div>
            )}

            {item.reviewStatus === 'approved' ? (
              <span className="badge badge-resolved" title={item.reviewByName ? `Approved by ${item.reviewByName}` : undefined}>
                <Icon name="check" size={12} /> Approved
              </span>
            ) : item.reviewStatus === 'changes_requested' ? (
              <span className="badge badge-progress">Changes requested</span>
            ) : null}
            {canComment && item.reviewStatus !== 'approved' && (
              <button type="button" className="btn btn-primary btn-sm" disabled={reviewBusy} onClick={() => setReview('approved')}>
                <Icon name="check" /> Approve
              </button>
            )}
            {canComment && (
              <Menu
                label="Review status"
                trigger={<Icon name="more" />}
                items={[
                  { label: 'Approve', icon: 'check', disabled: item.reviewStatus === 'approved', onSelect: () => setReview('approved') },
                  { label: 'Request changes', icon: 'pencil', disabled: item.reviewStatus === 'changes_requested', onSelect: () => setReview('changes_requested') },
                  { label: 'Back to in review', icon: 'refresh', disabled: item.reviewStatus === 'in_review', onSelect: () => setReview('in_review') },
                ]}
              />
            )}
            <button type="button" className="btn btn-secondary btn-sm side-toggle" aria-label={`Revisions, ${comments.filter((c) => c.status !== 'resolved').length} open`} onClick={() => setSideOpen(true)}>
              <Icon name="message" /> {comments.filter((c) => c.status !== 'resolved').length}
            </button>
          </div>
        </div>

        <div className="review-body">
          {item.type === 'website' ? (
            <WebsiteCanvas {...shared} viewport={viewport} onViewportChange={setViewport} />
          ) : !version ? (
            <ErrorState title="No file yet" message="This item has no uploaded version." />
          ) : item.type === 'image' ? (
            <ImageCanvas {...shared} version={version} />
          ) : (
            <VideoCanvas {...shared} version={version} />
          )}

          <CommentSidebar
            item={item}
            projectId={project.id}
            people={people}
            comments={comments}
            loading={commentsLoading}
            error={commentsError}
            onRetry={() => void reloadComments()}
            filter={filter}
            onFilterChange={setFilter}
            selectedId={selectedId}
            highlightId={highlightId}
            canComment={canComment}
            canManage={canManage}
            currentUserId={user?.id ?? null}
            currentVersionId={item.type === 'website' ? null : (version?.id ?? null)}
            actions={actions}
            linkFor={linkFor}
            onSelect={select}
            onHover={setHighlightId}
            open={sideOpen}
            onClose={() => setSideOpen(false)}
          />
        </div>
      </div>

      {uploading && (
        <NewVersionDialog
          item={item}
          onClose={() => setUploading(false)}
          onDone={(next) => {
            loaded.setData((current) => current && { ...current, item: next });
            knownLatest.current = next.currentVersion?.id ?? null; // our own upload: no "new version" notice
            setVersionId(next.currentVersion?.id ?? null);
            setUploading(false);
            toast.show(`Version ${next.currentVersion?.versionNo} uploaded`);
          }}
        />
      )}
    </div>
  );
}
